import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, 
  Search, 
  User, 
  CheckCircle2,
  ArrowRight,
  X,
  Loader2,
  MessageCircle,
  Send,
  Copy,
  Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { useAuthContext } from '../contexts/AuthContext';
import { getRegisteredUsers } from '../api/palm';
import { LoadingSpinner } from '../components/StateComponents';
import { 
  fadeInUp, 
  staggerContainer, 
  staggerItem,
  modalBackdrop,
  modalContent,
  successCheck
} from '../lib/animations';
import { ConfettiEffect } from '../components/premium';
import { transfer } from '../api/transfer';

const AnimatedSendMoney = () => {
  const navigate = useNavigate();
  const { selectedWallet } = useWallet();
  const { user } = useAuthContext();
  const [step, setStep] = useState(1); // 1: Contact, 2: Amount, 3: Review, 4: Success
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Fetch users on component mount and when search query changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getRegisteredUsers(searchQuery, user?.id);
        if (response.success) {
          setContacts(response.users || []);
        } else {
          setError(response.error || 'Failed to fetch users');
          setContacts([]);
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err.message || 'Failed to load users');
        setContacts([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchUsers();
    }, searchQuery ? 300 : 0); // Only debounce if there's a search query

    return () => clearTimeout(timeoutId);
  }, [searchQuery, user?.id]);

  // Filter contacts client-side as well (for additional filtering if needed)
  const filteredContacts = contacts.filter(contact =>
    !searchQuery || 
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.upiId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.phone?.includes(searchQuery)
  );

  // Check if search query looks like an email or phone number
  const isEmailOrPhone = (query) => {
    if (!query || query.trim() === '') return false;
    const trimmed = query.trim();
    // Check if it's an email (contains @)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Check if it's a phone number (contains mostly digits, with optional +, -, spaces, parentheses)
    const phoneRegex = /^[\+]?[\d\s\-\(\)]{7,}$/;
    return emailRegex.test(trimmed) || phoneRegex.test(trimmed);
  };

  // Check if it's specifically an email
  const isEmail = (query) => {
    if (!query) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(query.trim());
  };

  // Check if we should show invite (no results + looks like email/phone)
  const shouldShowInvite = !loading && !error && filteredContacts.length === 0 && isEmailOrPhone(searchQuery);

  // Invite message
  const inviteMessage = "I'm using PalmPe to send money securely using palm authentication.\n\nPlease register on www.palmpe.app to receive payments.";
  const appLink = "https://www.palmpe.app";

  // Extract phone number from query (removes all non-digit characters except +)
  const extractPhoneNumber = (query) => {
    if (!query) return '';
    // Keep + at the start if present, then extract digits
    const hasPlus = query.trim().startsWith('+');
    const digits = query.replace(/[^\d]/g, '');
    return hasPlus ? `+${digits}` : digits;
  };

  // Share functions
  const handleShareWhatsApp = () => {
    const phone = extractPhoneNumber(searchQuery);
    if (!phone) return;
    const message = encodeURIComponent(`${inviteMessage}\n\n${appLink}`);
    const whatsappUrl = `https://wa.me/${phone.replace(/\+/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareSMS = () => {
    const phone = extractPhoneNumber(searchQuery);
    if (!phone) return;
    const message = encodeURIComponent(`${inviteMessage} ${appLink}`);
    const smsUrl = `sms:${phone}?body=${message}`;
    window.location.href = smsUrl;
  };

  const handleCopyLink = async () => {
    const fullMessage = `${inviteMessage}\n\n${appLink}`;
    try {
      await navigator.clipboard.writeText(fullMessage);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullMessage;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setStep(2);
  };

  const handleAmountSubmit = () => {
    if (amount && parseFloat(amount) > 0) {
      setStep(3);
    }
  };

  const handlePaymentConfirm = async () => {
    try {
      setIsProcessingPayment(true);
      setError(null);
      
      const transferData = {
        receiverEmail: selectedContact.email,
        receiverPhone: selectedContact.phone,
        amount: amount,
        userId: user?.id
      };
      
      const response = await transfer(transferData);
      
      if (response.success) {
        setStep(4);
        setShowConfetti(true);
      } else {
        setError(response.error || 'Failed to transfer money');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to transfer money. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setSelectedContact(null);
    setAmount('');
    setNote('');
    setShowConfetti(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-20">
      {/* Header */}
      <motion.div 
        className="bg-gradient-to-br from-[#586BFF] via-[#6B7AFF] to-[#8B8FFF] text-white px-5 py-6 shadow-lg"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <div className="flex items-center gap-4">
          <motion.button 
            onClick={() => step === 1 ? navigate(-1) : setStep(step - 1)}
            className="p-2 hover:bg-white/10 rounded-full transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <div>
            <h1 className="text-xl font-bold">Send Money</h1>
            <motion.p 
              className="text-xs text-white/80"
              key={step}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {step === 1 && 'Select recipient'}
              {step === 2 && 'Enter amount'}
              {step === 3 && 'Review payment'}
              {step === 4 && 'Payment successful'}
            </motion.p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex gap-2 mt-4">
          {[1, 2, 3, 4].map((s) => (
            <motion.div
              key={s}
              className="flex-1 h-1 rounded-full bg-white/20"
              initial={{ scaleX: 0 }}
              animate={{ 
                scaleX: s <= step ? 1 : 0,
                backgroundColor: s <= step ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)'
              }}
              transition={{ duration: 0.3, delay: s * 0.1 }}
              style={{ transformOrigin: 'left' }}
            />
          ))}
        </div>
      </motion.div>

      {/* Step 1: Contact Selection */}
      <AnimatePresence mode="sync">
        {step === 1 && (
          <motion.div
            key="step1"
            className="px-5 py-6"
            {...fadeInUp}
            exit={{ opacity: 0, x: -100 }}
          >
            {/* Animated Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-4 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Search by name or UPI ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </Card>
            </motion.div>

            {/* Contacts List with Stagger */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#586BFF]" />
              </div>
            ) : error ? (
              <Card className="p-6 text-center">
                <p className="text-red-500 mb-2">Error loading users</p>
                <p className="text-sm text-gray-500">{error}</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="mt-4"
                  variant="outline"
                >
                  Retry
                </Button>
              </Card>
            ) : shouldShowInvite || filteredContacts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <Card className="p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Invite to PalmPe</h3>
                    <p className="text-sm text-gray-600">
                      This contact isn't registered yet. Invite them to join PalmPe!
                    </p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-700 whitespace-pre-line text-center">
                        {inviteMessage}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Action:</p>
                    <p className="text-xs text-gray-500 mb-4">• Share PalmPe app link via:</p>
                    
                    {/* WhatsApp */}
                    <div>
                      <Button
                        onClick={handleShareWhatsApp}
                        className="w-full bg-green-500 hover:bg-green-600 text-white h-12 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isEmail(searchQuery)}
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>WhatsApp</span>
                      </Button>
                      {isEmail(searchQuery) && (
                        <p className="text-xs text-gray-400 mt-1 text-center">Available for phone numbers only</p>
                      )}
                    </div>

                    {/* SMS */}
                    <div>
                      <Button
                        onClick={handleShareSMS}
                        className="w-full bg-blue-500 hover:bg-blue-600 text-white h-12 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isEmail(searchQuery)}
                      >
                        <Send className="w-5 h-5" />
                        <span>SMS</span>
                      </Button>
                      {isEmail(searchQuery) && (
                        <p className="text-xs text-gray-400 mt-1 text-center">Available for phone numbers only</p>
                      )}
                    </div>

                    {/* Copy Link */}
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      className="w-full h-12 flex items-center justify-center gap-2 border-2"
                    >
                      {linkCopied ? (
                        <>
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-green-500">Link Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          <span>Copy Link</span>
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="initial"
                animate="animate"
                className="space-y-2"
              >
                {filteredContacts.map((contact) => (
                  <motion.div
                    key={contact.id}
                    variants={staggerItem}
                    whileHover={{ scale: 1.02, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card 
                      className="p-4 cursor-pointer hover:shadow-lg transition-all"
                      onClick={() => handleContactSelect(contact)}
                    >
                      <div className="flex items-center gap-3">
                        <motion.div 
                          className="w-12 h-12 rounded-full bg-gradient-to-br from-[#586BFF] to-[#9B62FF] flex items-center justify-center text-white font-bold"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                        >
                          {contact.avatar || contact.name.charAt(0).toUpperCase()}
                        </motion.div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-800">{contact.name}</p>
                            {contact.hasPalmRegistered && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Palm
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {contact.upiId || contact.email || contact.phone || 'No contact info'}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Step 2: Amount Entry */}
        {step === 2 && (
          <motion.div
            key="step2"
            className="px-5 py-6"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
          >
            {/* Selected Contact */}
            {selectedContact && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#586BFF] to-[#9B62FF] flex items-center justify-center text-white font-bold">
                      {selectedContact.avatar || selectedContact.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{selectedContact.name}</p>
                      <p className="text-xs text-gray-500">{selectedContact.upiId || selectedContact.email || selectedContact.phone || ''}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Amount Input with Number Ticker */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 mb-4 relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-[#586BFF]/5 to-[#9B62FF]/5"
                  animate={{ opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <div className="relative z-10">
                  <p className="text-sm text-gray-600 mb-2">Enter Amount</p>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold text-gray-800">₹</span>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0"
                      className="text-3xl font-bold border-0 focus:ring-0 p-0 h-auto"
                    />
                  </div>
                  <motion.p 
                    className="text-xs text-gray-500 mt-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    Available: ₹{(selectedWallet?.balance || 0).toLocaleString()}
                  </motion.p>
                </div>
              </Card>
            </motion.div>

            {/* Note Input */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="p-4 mb-6">
                <Input
                  placeholder="Add a note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Card>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={handleAmountSubmit}
                disabled={!amount || parseFloat(amount) <= 0}
                className="w-full bg-gradient-to-r from-[#586BFF] to-[#9B62FF] h-12 text-base font-semibold shadow-lg"
              >
                Continue
              </Button>
            </motion.div>
          </motion.div>
        )}

        {/* Step 3: Review (3D Flip Card) */}
        {step === 3 && (
          <motion.div
            key="step3"
            className="px-5 py-6"
            initial={{ opacity: 0, rotateY: 90 }}
            animate={{ opacity: 1, rotateY: 0 }}
            exit={{ opacity: 0, rotateY: -90 }}
            transition={{ duration: 0.5 }}
            style={{ transformStyle: "preserve-3d" }}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
            >
              <Card className="p-6 mb-6 bg-gradient-to-br from-[#586BFF] to-[#9B62FF] text-white shadow-2xl">
                <h3 className="text-lg font-bold mb-4">Review Payment</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-white/80">To</span>
                    <div className="text-right">
                      <p className="font-semibold">{selectedContact?.name || 'Unknown'}</p>
                      <p className="text-xs text-white/70">{selectedContact?.upiId || selectedContact?.email || selectedContact?.phone || ''}</p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-white/80">Amount</span>
                    <motion.p 
                      className="text-2xl font-bold"
                      initial={{ scale: 1.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                    >
                      ₹{amount}
                    </motion.p>
                  </div>

                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-white/80">From</span>
                    <div className="text-right">
                      <p className="font-semibold">{selectedWallet?.name || 'Wallet'}</p>
                      <p className="text-xs text-white/70">Balance: ₹{(selectedWallet?.balance || 0).toLocaleString()}</p>
                    </div>
                  </div>

                  {note && (
                    <div className="flex justify-between items-center">
                      <span className="text-white/80">Note</span>
                      <p className="text-sm">{note}</p>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
              >
                <Card className="p-4 bg-red-50 border-red-200">
                  <p className="text-sm text-red-600">{error}</p>
                </Card>
              </motion.div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <motion.div whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="w-full h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isProcessingPayment}
                >
                  Edit
                </Button>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  onClick={handlePaymentConfirm}
                  disabled={isProcessingPayment}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 h-12 font-semibold shadow-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    'Confirm & Pay'
                  )}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Success with Confetti */}
        {step === 4 && (
          <motion.div
            key="step4"
            className="px-5 py-6 flex flex-col items-center justify-center min-h-[60vh]"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            {/* Confetti Effect */}
            {showConfetti && (
              <div className="fixed inset-0 pointer-events-none z-50">
                {[...Array(50)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: ['#586BFF', '#9B62FF', '#00C8D6', '#FFD700'][i % 4],
                      left: `${Math.random() * 100}%`,
                      top: '-10%'
                    }}
                    animate={{
                      y: ['0vh', '110vh'],
                      x: [0, (Math.random() - 0.5) * 200],
                      rotate: [0, 360 * (Math.random() > 0.5 ? 1 : -1)],
                      opacity: [1, 0]
                    }}
                    transition={{
                      duration: 2 + Math.random() * 2,
                      delay: Math.random() * 0.5,
                      ease: 'easeOut'
                    }}
                  />
                ))}
              </div>
            )}

            {/* Success Checkmark */}
            <motion.div
              variants={successCheck}
              initial="initial"
              animate="animate"
              className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mb-6 shadow-2xl"
            >
              <CheckCircle2 className="w-16 h-16 text-white" />
            </motion.div>

            <motion.h2 
              className="text-2xl font-bold text-gray-800 mb-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Payment Successful!
            </motion.h2>

            <motion.p 
              className="text-gray-600 mb-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              ₹{amount} sent to {selectedContact?.name || 'recipient'}
            </motion.p>

            <motion.div
              className="w-full space-y-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Button
                onClick={() => navigate('/history')}
                className="w-full bg-gradient-to-r from-[#586BFF] to-[#9B62FF] h-12"
              >
                View Transaction
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                className="w-full h-12"
              >
                Send Again
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confetti Effect */}
      <ConfettiEffect active={showConfetti} duration={3000} />
    </div>
  );
};

export default AnimatedSendMoney;
